import React, { FunctionComponent, useState } from 'react';
import { Button, Container } from 'semantic-ui-react';
import withStyles from 'react-jss';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { compose } from 'recompose';
import { Redirect, match, withRouter } from 'react-router-dom';
import { pathOr, isEmpty } from 'ramda';

import { Theme } from 'config';
import { ClassProp } from 'helpers/types';
import { GET_PROFILE } from 'components/UserMgmt/UserMgmt.queries';
import { useUserMgmt } from 'components/UserMgmt/UserMgmt.context';
import { useGetMerchantVenueCounts } from 'helpers/useMerchantVenueCounts';
import {
  Place,
  getSelectedPlaces,
} from 'scenes/Home/scenes/MerchantVenuesOnboarding/sections/MerchantOnboardVenues/MerchantOnboardVenues.helpers';

import { ONBOARD_MERCHANT_VENUES } from './OnboardSelectedVenues.queries';

type Styles = ClassProp<ReturnType<typeof styles>>;

type Props = {
  classes: Styles;
  places: Place[];
  match: match;
  placeIds: string[];
};

type OnboardMerchantVenuesResponse = {
  addPlacesToMerchantVenues: {
    successful: boolean;
  };
};

const styles = (theme: Theme) => ({
  container: {
    height: '100% !important',
  },
  submitButton: {
    float: 'right',
    backgroundColor: `${theme.violet} !important`,
    borderRadius: '1.5rem !important',
    color: '#fff !important',
    fontSize: '.875rem',
    margin: '.25rem 0 !important',
    opacity: 1,
    padding: '.6625rem 1.5em .6625rem !important',
    '&:hover': {
      opacity: 0.9,
    },
    '&:active': {
      opacity: 0.7,
    },
  },
  selectAllHeader: {
    paddingTop: '2rem',
  },
});

const cleanupTypenameFromPlaces = (places: Place[]): Place[] =>
  JSON.parse(JSON.stringify(places), (key: any, value: any) =>
    key === '__typename' ? undefined : value,
  );

const OnboardVenues: FunctionComponent<Props> = ({
  places,
  placeIds,
  classes,
  match,
}) => {
  const { isTier3, isExternalUser } = useUserMgmt();
  const { venueCounts } = useGetMerchantVenueCounts(match);

  const [redirect, setRedirect] = useState<boolean>(false);
  const userProfileResponse = useQuery(GET_PROFILE);
  const {
    data: { userProfile },
    loading,
  } = userProfileResponse;

  const { merchantId, merchantName }: any =
    isEmpty(match.params) || isExternalUser || isTier3
      ? userProfile
      : match.params;

  const handleOnboardVenueResponse = (
    onboardCallResponse: OnboardMerchantVenuesResponse,
  ) => {
    const onboardSuccessful = pathOr(
      false,
      ['addPlacesToMerchantVenues', 'successful'],
      onboardCallResponse,
    );

    if (onboardSuccessful) {
      setRedirect(true);
    }
  };

  const handleOnboardMerchantVenues = () => {
    onboardMerchantVenues({
      variables: {
        merchantUuid: merchantId,
        placeDescriptions: cleanupTypenameFromPlaces(
          getSelectedPlaces(places, placeIds),
        ),
      },
    });
  };

  const [
    onboardMerchantVenues,
    { loading: submittingPlacesToOnboard },
  ] = useMutation(ONBOARD_MERCHANT_VENUES, {
    onCompleted: handleOnboardVenueResponse,
  });

  if (loading) return null;
  if (redirect) {
    let url = '';

    if (isTier3 && venueCounts === 0) {
      url = `/${merchantName}/success/onboarding`;
    } else if (isExternalUser) {
      url = `/${merchantName}/overview`;
    } else {
      url = `/merchant/${merchantId}/overview/${merchantName}`;
    }

    return <Redirect to={`${url}`} push />;
  }

  return (
    <Container>
      <Button
        className={classes.submitButton}
        disabled={places.length === 0}
        onClick={handleOnboardMerchantVenues}
      >
        {submittingPlacesToOnboard ? '...submitting' : 'Add My Locations'}
      </Button>
    </Container>
  );
};

export default compose<Props, any>(
  withRouter,
  withStyles(styles),
)(OnboardVenues);
